/**
 * What the Institute calls the ceremony a successful upgrade ends at, and
 * therefore what the bill for it is called.
 *
 * A Member is INDUCTED; a Fellow is INVESTED. Two different ceremonies with two
 * different names, and an applicant reading "induction fees" on a fellowship
 * upgrade is reading about somebody else's event.
 *
 * The words live here rather than inline on each screen because the same bundle
 * is named on the applications list, on the application's own page and on the
 * button between them — and a button that says "Pay investiture fee" leading to
 * a card headed "Induction fees" is worse than either word used consistently.
 *
 * Only the WORDS change. It is one charge, one invoice and one payment either
 * way (`UpgradeInductionService`, `source = 'upgrade_induction'`), and nothing
 * here is sent to the server or read back from it.
 */
export type Ceremony = {
  /** "induction" / "investiture" — mid-sentence, lower case. */
  noun: string;
  /** The button: "Pay induction fees" / "Pay investiture fees". */
  payLabel: string;
  /** A heading: "Induction fees due" / "Investiture fees due". */
  feesTitle: (paid: boolean) => string;
  /** The event the bundle books a seat at. */
  eventNoun: string;
};

const INDUCTION: Ceremony = {
  noun: "induction",
  payLabel: "Pay induction fees",
  feesTitle: (paid) => (paid ? "Induction fees paid" : "Induction fees due"),
  eventNoun: "induction event",
};

const INVESTITURE: Ceremony = {
  noun: "investiture",
  payLabel: "Pay investiture fees",
  feesTitle: (paid) => (paid ? "Investiture fees paid" : "Investiture fees due"),
  eventNoun: "investiture",
};

/**
 * The vocabulary for an upgrade to a given grade.
 *
 * Matched on the grade NAME, which is the convention the rest of the upgrade
 * code uses for grade-specific rules. Anything that is not a fellowship upgrade
 * — Member above all, but also Probationer — keeps "induction", which is what
 * every one of these screens said before fellowship upgrades needed their own
 * word, so no existing screen changes meaning.
 */
export function ceremonyFor(toGrade?: string | null): Ceremony {
  return String(toGrade ?? "").trim().toLowerCase() === "fellow" ? INVESTITURE : INDUCTION;
}

/** The grade an upgrade row or application is heading TO. */
export function toGradeOf(app: any): string | null {
  return app?.to_category?.name ?? app?.to_grade ?? null;
}
